/**
 * Mis documentos (PRL).
 *
 * Formación, reconocimiento médico, certificados. Lo que importa aquí no es el
 * documento sino su caducidad: en algunos centros, con un documento caducado no
 * se puede entrar (interlock de PRL). Así que el estado va primero y en grande.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  getDocumentStatus,
  getDocumentTypes,
  uploadDocument,
  DocumentStatusResponse,
  DocumentType,
  UserDocumentSummary,
  apiMessage,
} from '../services/apiClient';
import { Icon, IconName } from './ui/Icon';

type Look = { icon: IconName; color: string; soft: string; label: string; border: boolean };

function look(doc: UserDocumentSummary): Look {
  switch (doc.status) {
    case 'EXPIRED':
      return { icon: 'alert', color: 'var(--danger)', soft: 'var(--danger-soft)', label: 'CADUCADO', border: true };
    case 'WARNING_2':
      return { icon: 'alert', color: 'var(--signal)', soft: 'var(--signal-soft)', label: 'CADUCA YA', border: true };
    case 'WARNING_1':
      return { icon: 'info', color: 'var(--warn)', soft: 'var(--warn-soft)', label: 'PRONTO', border: true };
    case 'PENDING':
      return { icon: 'clock', color: 'var(--ink-3)', soft: 'var(--surface-2)', label: 'EN REVISIÓN', border: false };
    default:
      return { icon: 'shield', color: 'var(--brand)', soft: 'var(--brand-soft)', label: 'VIGENTE', border: false };
  }
}

function expiryText(doc: UserDocumentSummary): string {
  if (doc.status === 'EXPIRED') return 'Caducado · renuévalo';
  if (doc.status === 'PENDING') return 'Pendiente de validar por tu responsable';
  if (doc.days_remaining != null && doc.days_remaining <= 60) {
    return `Caduca en ${doc.days_remaining} ${doc.days_remaining === 1 ? 'día' : 'días'}`;
  }
  if (doc.expiry_date) {
    return `Válido hasta ${new Date(doc.expiry_date).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })}`;
  }
  return 'Sin caducidad';
}

export function DocumentStatus() {
  const [status, setStatus] = useState<DocumentStatusResponse | null>(null);
  const [types, setTypes] = useState<DocumentType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<number | null>(null);
  const [declaring, setDeclaring] = useState<number | null>(null);
  const [expiry, setExpiry] = useState('');
  const [fichero, setFichero] = useState<File | null>(null);

  const load = useCallback(async () => {
    const [s, t] = await Promise.all([getDocumentStatus(), getDocumentTypes()]);
    setStatus(s);
    setTypes(t);
  }, []);

  useEffect(() => {
    load().catch(() => setError('No se pudieron cargar tus documentos'));
  }, [load]);

  // La caducidad es OBLIGATORIA —de ella vive el radar— y el fichero
  // opcional. Antes esta pantalla mandaba un base64 sin fecha contra un
  // endpoint que no aceptaba ficheros: 422 siempre, "No se pudo subir el
  // documento", cada vez.
  const declarar = async (typeId: number) => {
    setError(null);
    setUploading(typeId);
    try {
      let base64: string | undefined;
      if (fichero) {
        base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(fichero);
        });
      }

      await uploadDocument({
        document_type_id: typeId,
        expiry_date: expiry,
        file_base64: base64,
      });
      setDeclaring(null);
      setExpiry('');
      setFichero(null);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo registrar el documento'));
    } finally {
      setUploading(null);
    }
  };

  const docs = status?.documents ?? [];
  const missing = types.filter((t) => !docs.some((d) => d.document_type_id === t.id));

  return (
    <div className="p-4 space-y-4">
      <p className="text-ink-2 text-[15px]">
        Si un documento caduca, en algunos centros no podrás entrar.
      </p>

      {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14.5px]">{error}</p>}

      <div className="space-y-3">
        {docs.map((doc) => {
          const l = look(doc);
          return (
            <div
              key={doc.id}
              className="flex items-center gap-3.5 rounded-2xl bg-surface p-4"
              style={{ border: `1px solid ${l.border ? l.color : 'var(--line)'}` }}
            >
              <span
                className="w-[42px] h-[42px] rounded-xl grid place-items-center shrink-0"
                style={{ background: l.soft }}
              >
                <Icon name={l.icon} size={21} style={{ color: l.color }} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[15.5px] truncate">{doc.type_name}</p>
                <p className="text-[13px]" style={{ color: l.border ? l.color : 'var(--ink-2)' }}>
                  {expiryText(doc)}
                </p>
              </div>
              <span className="font-mono text-[11px] font-semibold shrink-0" style={{ color: l.color }}>
                {l.label}
              </span>
            </div>
          );
        })}

        {docs.length === 0 && !error && (
          <p className="text-center text-[14px] text-ink-3 py-8">No tienes documentos registrados</p>
        )}
      </div>

      {missing.length > 0 && (
        <div>
          <p className="font-mono text-[12px] uppercase tracking-wider text-ink-3 mb-2.5">Te faltan</p>
          <div className="space-y-2.5">
            {missing.map((t) => (
              <div
                key={t.id}
                className="rounded-2xl bg-surface border border-dashed border-line-strong p-4"
              >
                <div className="flex items-center gap-3.5">
                  <span className="w-[42px] h-[42px] rounded-xl bg-surface-2 grid place-items-center shrink-0">
                    <Icon name="file" size={21} className="text-ink-3" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-[15.5px] truncate">{t.name}</span>
                    <span className="block text-[13px] text-ink-2">
                      {t.is_blocking ? 'Sin él no podrás fichar entrada' : 'Aún no registrado'}
                    </span>
                  </span>
                  {declaring !== t.id && (
                    <button
                      onClick={() => {
                        setDeclaring(t.id);
                        setExpiry('');
                        setFichero(null);
                      }}
                      className="h-10 px-4 rounded-xl bg-brand text-on-brand font-bold text-[14px] shrink-0"
                    >
                      Registrar
                    </button>
                  )}
                </div>

                {declaring === t.id && (
                  <div className="mt-3.5 flex flex-wrap gap-2.5 items-end">
                    <label className="block flex-1 min-w-[180px]">
                      <span className="label">¿Hasta cuándo es válido?</span>
                      <input
                        type="date"
                        className="field"
                        value={expiry}
                        onChange={(e) => setExpiry(e.target.value)}
                      />
                    </label>
                    <label className="block flex-1 min-w-[180px]">
                      <span className="label">Foto o PDF (opcional)</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        className="field h-auto py-2"
                        onChange={(e) => setFichero(e.target.files?.[0] ?? null)}
                      />
                      <span className="block text-[12px] text-ink-3 mt-1">
                        Lo ve tu responsable para validarlo. Máximo 8 MB.
                      </span>
                    </label>
                    <button
                      onClick={() => setDeclaring(null)}
                      className="h-12 px-4 rounded-xl border border-line-strong font-semibold text-[14px]"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => declarar(t.id)}
                      disabled={!expiry || uploading !== null}
                      className="h-12 px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px] disabled:opacity-40"
                    >
                      {uploading === t.id ? 'Guardando…' : 'Guardar'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
